/* eslint-disable @typescript-eslint/naming-convention */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode,
} from "react";
import { useAuth } from "./useAuth";
import getMe, { MeRole } from "#/apis/infra/getMe";
import listLicenses, { LicenseItem } from "#/apis/infra/listLicenses";
import patchMePreferences from "#/apis/infra/patchMePreferences";
import { isGatewayIdp, setActiveLicenseId, getActiveLicenseId } from "./idp";

// Gate states consumed by ProtectedRoute (App.tsx). "Org" is the UI term for the internal
// LICENSE scope (X-License-Id); see RBAC terminology.
//   pending    → /me in flight, render nothing (no protected page mounts → no API fires)
//   ready      → an active Organization (X-License-Id) is set → render the app
//   needs-org  → /me 200 but no default org and >1 accessible → show the Organization picker
//   forbidden  → /me 403, or 200 with zero accessible orgs → /no-access
//   error      → /me failed transiently (5xx / network) → retry screen (NOT /no-access)
export type OrgGateState = "pending" | "ready" | "needs-org" | "forbidden" | "error";

type OrgContextValue = {
  gate: OrgGateState;
  orgs: LicenseItem[];
  activeOrgId: string | null;
  defaultOrgId: string | null;
  // Caller identity + effective roles from GET /me, retained for RBAC gating (see useRbac).
  roles: MeRole[];
  userId: string | null;
  // True once GET /me has resolved — lets RBAC gating distinguish "roles not loaded yet"
  // (Cognito fetches them in the background) from "loaded and not a manager".
  meLoaded: boolean;
  switchOrg: (id: string) => void;
  setDefaultOrg: (id: string) => Promise<boolean>;
  refreshOrgs: () => void;
  retry: () => void;
};

const OrgContext = createContext<OrgContextValue | null>(null);

export const useOrg = (): OrgContextValue => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be used within <OrgProvider>");
  return ctx;
};

export const OrgProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [gate, setGate] = useState<OrgGateState>(isGatewayIdp ? "pending" : "ready");
  const [orgs, setOrgs] = useState<LicenseItem[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(getActiveLicenseId());
  const [defaultOrgId, setDefaultOrgId] = useState<string | null>(null);
  const [roles, setRoles] = useState<MeRole[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);
  const [nonce, setNonce] = useState(0);

  // Read the freshest token without re-running the bootstrap on a silent token renew.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const gateRef = useRef<OrgGateState>(gate);
  gateRef.current = gate;

  const loadOrgs = useCallback(async (tk: string): Promise<void> => {
    const res = await listLicenses({ token: tk });
    if (res.success && res.data) setOrgs(res.data.items);
  }, []);

  // Bootstrap the active Organization from GET /me. Keyed on auth state + a retry nonce only
  // (NOT the rotating token), so a mid-flight silent renew cannot abort it and wedge "pending".
  useEffect(() => {
    const tk = tokenRef.current;
    if (!auth.isAuthenticated || !tk) {
      setMeLoaded(false);
      setGate(isGatewayIdp ? "pending" : "ready");
      return undefined;
    }

    let alive = true;

    // Cognito (AWS): no org/license gating — render immediately. The internal API (1.5.3 = the
    // AWS spec) does expose /me, so fetch it in the BACKGROUND purely to populate roles for RBAC.
    // Fail-open: any failure leaves roles empty and the app unaffected; a Cognito /me 401 does
    // NOT bounce to /login (see request.ts). This is what lets the RBAC pages work on AWS too.
    if (!isGatewayIdp) {
      setGate("ready");
      getMe({ token: tk }).then((me) => {
        if (!alive) return;
        if (me.success && me.data) {
          setRoles(me.data.access_summary.roles ?? []);
          setUserId(me.data.user.user_id);
        }
        setMeLoaded(true);
      });
      return () => { alive = false; };
    }

    setGate("pending");
    (async (): Promise<void> => {
      const me = await getMe({ token: tk });
      if (!alive) return;
      setMeLoaded(true);
      if (!me.success || !me.data) {
        // 403 = no access (→ /no-access). 401 (expired) is handled by the request interceptor
        // (→ /login). Anything else (5xx / network) is transient → "error" + retry screen.
        setGate(me.statusCode === 403 ? "forbidden" : "error");
        return;
      }
      // Retain caller identity + roles for RBAC gating (used by useRbac).
      setRoles(me.data.access_summary.roles ?? []);
      setUserId(me.data.user.user_id);

      const { default_license_id: def, license_count: count } = me.data.access_summary;
      const stored = getActiveLicenseId();

      if (count === 0) {
        // Zero accessible orgs → genuinely no access. Clear any stale stored id so it can't leak.
        setActiveLicenseId(null);
        setActiveOrgId(null);
        setGate("forbidden");
        return;
      }

      // Load the user's accessible licenses so we can VALIDATE any stored selection before it
      // becomes the active X-License-Id. localStorage is shared across all tabs of this origin, so
      // another tab/user's license can linger there; trusting it blindly sends an inaccessible
      // X-License-Id and 403s every call. Settle the active license BEFORE "ready".
      const res = await listLicenses({ token: tk });
      if (!alive) return;
      const listOk = res.success && !!res.data;
      const items = listOk && res.data ? res.data.items : [];
      if (listOk) setOrgs(items);
      const inList = (id: string | null): boolean => !!id && items.some((o) => o.license_id === id);

      if (def) setDefaultOrgId(def);

      // Priority: a still-valid stored pick (a deliberate mid-session switch survives reload) >
      // the /me default > a lone usable org. If the list failed to load we can't validate, so we
      // trust /me's default rather than block the user.
      let active: string | null = null;
      if (!listOk) active = def ?? stored;
      else if (inList(stored)) active = stored;
      else if (inList(def)) active = def;
      else if (items.length === 1 && items[0].license_status === "enable") active = items[0].license_id;

      if (active) {
        setActiveLicenseId(active);
        setActiveOrgId(active);
        setGate("ready");
      } else {
        // Stale stored / invalid default / multi-org with no pick → clear stale and prompt.
        if (stored && !inList(stored)) setActiveLicenseId(null);
        setActiveOrgId(null);
        setGate("needs-org");
      }
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, nonce]);

  const retry = useCallback((): void => setNonce((n) => n + 1), []);

  // Switch the active org for this session. Behavior A (xcockpit): switching does NOT change
  // the default — that is a separate action (setDefaultOrg).
  const switchOrg = useCallback((id: string): void => {
    // Re-selecting the already-active org outside the first-pick flow is a no-op (no reload).
    if (id === getActiveLicenseId() && gateRef.current !== "needs-org") return;
    setActiveLicenseId(id);
    setActiveOrgId(id);
    if (gateRef.current === "needs-org") {
      // First pick: unblock and let protected pages mount now (with X-License-Id already set).
      setGate("ready");
    } else {
      // Already inside the app → reload so every page refetches under the new org.
      window.location.assign("/dashboard");
    }
  }, []);

  // Re-fetch the accessible org list (e.g. after editing an org's name/status). No-op if no token.
  const refreshOrgs = useCallback((): void => {
    const tk = tokenRef.current;
    if (tk) loadOrgs(tk);
  }, [loadOrgs]);

  // Persist the default org (xcockpit "Set as Homepage Organization"). Does not switch.
  // Returns whether the PATCH succeeded so the caller can toast success vs failure.
  const setDefaultOrg = useCallback(async (id: string): Promise<boolean> => {
    const tk = tokenRef.current;
    if (!tk) return false;
    const res = await patchMePreferences({ token: tk, defaultLicenseId: id });
    if (res.success) setDefaultOrgId(id);
    return res.success;
  }, []);

  const value = useMemo<OrgContextValue>(() => ({
    gate,
    orgs,
    activeOrgId,
    defaultOrgId,
    roles,
    userId,
    meLoaded,
    switchOrg,
    setDefaultOrg,
    refreshOrgs,
    retry,
  }), [
    gate, orgs, activeOrgId, defaultOrgId, roles, userId, meLoaded,
    switchOrg, setDefaultOrg, refreshOrgs, retry,
  ]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};

export default OrgProvider;
